import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Replays the showcase conversation for shared-link visitors.
 *
 * WHY A PLAYBACK ENGINE AND NOT JUST A TRANSCRIPT
 *
 * The old demo dropped ~440 lines of mock messages into state in one call. Two
 * things went wrong. The autoscroll is sticky-to-bottom, so a visitor who had
 * read nothing landed at the *end* of a stranger's conversation — which is also
 * how they came to believe it was the project owner's real chat history. And
 * because every turn arrived already finished, none of the agentic surfaces —
 * the working state, the tool skeletons, the turn telemetry — ever rendered.
 * The demo advertised a product two releases old.
 *
 * Replaying from empty fixes both at once: the transcript grows downward under
 * a reader who is already at the top, and the agent is visibly *working*.
 *
 * HOW IT PLUGS IN
 *
 * This hook owns no transcript state of its own. It drives the chat page's
 * existing setters, so every message, card, chip and panel renders through the
 * exact same code a signed-in user hits — there is no second rendering path to
 * keep in sync.
 */

/**
 * Beat before the first user message. Long enough to actually read the intro
 * card, since the follow loop starts scrolling it away as soon as the
 * transcript overflows.
 */
const OPENING_DELAY_MS = 2600;
/** Beat between the last tool finishing and the working state clearing. */
const CLEANUP_DELAY_MS = 300;

/**
 * Flatten the script into a linear list of `{ delay, run }` beats.
 *
 * Message indices are computed here, walking the array exactly as the runner
 * will build it, because `turnMetaByIndex` and `pendingToolsByIndex` are keyed
 * by position — hand-numbering them would rot the moment a turn is inserted.
 */
function buildSteps(turns) {
    const steps = [];
    let cursor = 0;
    let leadIn = OPENING_DELAY_MS;

    turns.forEach((turn) => {
        cursor += 1; // the user turn
        const replyIndex = cursor;
        cursor += 1 + turn.toolRows.length; // the reply, then its tool rows

        const announced = (turn.announced || []).map((tool, i) => ({
            ...tool,
            status: i === 0 ? 'running' : 'queued',
        }));

        // The user speaks and the agent starts thinking.
        steps.push({
            delay: leadIn,
            run: (a) => {
                a.setMessages(prev => [...prev, turn.user]);
                a.setAnnouncedTools(null);
                a.setTurnStartedAt(Date.now());
                a.setActiveTurnContext({
                    modelLabel: turn.context?.modelLabel,
                    files: turn.context?.files || [],
                    tasks: turn.context?.tasks || [],
                    fullContext: !!turn.context?.fullContext,
                    webSearch: !!turn.context?.webSearch,
                    imageCount: turn.context?.imageCount || 0,
                });
                a.setLoading(true);
            },
        });

        // The reply lands and its tool calls are announced. `loading` stays
        // true through the tool phase, which is what swaps the prose skeleton
        // for the per-message tool cards — same as a live turn.
        steps.push({
            delay: turn.thinkingMs,
            run: (a) => {
                a.setMessages(prev => [...prev, turn.reply]);
                a.setTurnMetaByIndex(prev => ({ ...prev, [replyIndex]: turn.turnMeta }));
                if (announced.length > 0) {
                    a.setAnnouncedTools(announced);
                    a.setPendingToolsByIndex(prev => ({ ...prev, [replyIndex]: announced }));
                }
            },
        });

        // Each tool completes in turn, handing its persisted row to the
        // transcript. That row is what the page's rehydration effect reads to
        // build the artifact cards and the task-changes chip.
        announced.forEach((_, i) => {
            const next = announced.map((tool, j) => ({
                ...tool,
                status: j <= i ? 'done' : j === i + 1 ? 'running' : 'queued',
            }));
            const row = turn.toolRows[i];
            steps.push({
                delay: turn.toolStepMs,
                run: (a) => {
                    a.setPendingToolsByIndex(prev => ({ ...prev, [replyIndex]: next }));
                    a.setAnnouncedTools(next);
                    if (row) a.setMessages(prev => [...prev, row]);
                },
            });
        });

        // Any rows a turn declared without announcing a tool for them.
        const orphanRows = turn.toolRows.slice(announced.length);
        if (orphanRows.length > 0) {
            steps.push({
                delay: turn.toolStepMs,
                run: (a) => a.setMessages(prev => [...prev, ...orphanRows]),
            });
        }

        steps.push({
            delay: CLEANUP_DELAY_MS,
            run: (a) => {
                a.setLoading(false);
                a.setAnnouncedTools(null);
                a.setActiveTurnContext(null);
                a.setTurnStartedAt(null);
            },
        });

        leadIn = turn.settleMs ?? 1200;
    });

    return steps;
}

/**
 * @param {object}   options
 * @param {boolean}  options.enabled     Only shared-link visitors get a demo.
 * @param {object}   options.script      The lazily-imported showcase module.
 * @param {boolean}  options.reduceMotion
 * @param {object}   options.actions     The chat page's setters, plus onJumpToTop.
 * @returns {{ phase: 'loading'|'playing'|'done', skip: Function, replay: Function }}
 */
export function useShowcasePlayback({ enabled, script, reduceMotion, actions }) {
    const [phase, setPhase] = useState('loading');

    // Read through a ref so the driving effect depends only on what actually
    // restarts playback. Rebuilding it on every render of a 3900-line page
    // would tear the timer chain down mid-conversation.
    const actionsRef = useRef(actions);
    actionsRef.current = actions;

    const timerRef = useRef(null);
    const cursorRef = useRef(0);

    const steps = useMemo(
        () => (script ? buildSteps(script.SHOWCASE_TURNS) : null),
        [script]
    );

    const stop = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    /** Wipe every piece of turn state the engine writes. */
    const reset = useCallback(() => {
        const a = actionsRef.current;
        a.setMessages([]);
        a.setLoading(false);
        a.setAnnouncedTools(null);
        a.setActiveTurnContext(null);
        a.setTurnStartedAt(null);
        a.setTurnMetaByIndex({});
        a.setPendingToolsByIndex({});
    }, []);

    const runNext = useCallback(() => {
        const step = steps?.[cursorRef.current];
        if (!step) {
            timerRef.current = null;
            setPhase('done');
            return;
        }
        timerRef.current = setTimeout(() => {
            step.run(actionsRef.current);
            cursorRef.current += 1;
            runNext();
        }, step.delay);
    }, [steps]);

    /** Commit the whole conversation at once and park the reader at the start. */
    const skip = useCallback(() => {
        if (!script) return;
        stop();
        const a = actionsRef.current;
        a.setLoading(false);
        a.setAnnouncedTools(null);
        a.setActiveTurnContext(null);
        a.setTurnStartedAt(null);
        a.setPendingToolsByIndex({});
        a.setTurnMetaByIndex(script.buildShowcaseTurnMeta());
        a.setMessages(script.buildShowcaseMessages());
        cursorRef.current = steps.length;
        setPhase('done');
        a.onJumpToTop?.();
    }, [script, steps, stop]);

    const replay = useCallback(() => {
        if (!script) return;
        stop();
        reset();
        actionsRef.current.onResumeFollow?.();
        cursorRef.current = 0;
        setPhase('playing');
        runNext();
    }, [script, stop, reset, runNext]);

    useEffect(() => {
        if (!enabled || !steps) return undefined;

        cursorRef.current = 0;
        reset();

        // Reduced motion means no timed reveal at all — the whole conversation,
        // read from the top like a document.
        if (reduceMotion) {
            const a = actionsRef.current;
            a.setTurnMetaByIndex(script.buildShowcaseTurnMeta());
            a.setMessages(script.buildShowcaseMessages());
            cursorRef.current = steps.length;
            setPhase('done');
            a.onJumpToTop?.();
            return undefined;
        }

        setPhase('playing');
        runNext();
        return stop;
    }, [enabled, steps, script, reduceMotion, reset, runNext, stop]);

    // Belt and braces: a timer that outlives the page would setState on an
    // unmounted tree every second until the script ran out.
    useEffect(() => stop, [stop]);

    return { phase, skip, replay };
}
